/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021 (original work) Open Assessment Technologies SA ;
 */

/**
 * Svelte Action which adds IntersectionObserver for the element
 * Fires event when node comes into view, or when part of it becomes hidden.
 * Can be used to detect if parent element is fully scrolled up or down.
 * @param {DOMElement} node - the node implementing this action
 * @param {DOMElement|null} [scrollContainer] - scroll container:
 *    set to `null` if scroll is on document body;
 *    set to 'undefined' (not specified) if no need to add observer yet, or to remove existing one
 * @returns {Object} action
 * @fires visible - e.detail is Boolean - if element is visible or not
 */
export default function visibilityObserver(node, scrollContainer) {
    let observer = createObserver(node, scrollContainer);
    return {
        update(newScrollContainer) {
            if (newScrollContainer !== scrollContainer) {
                if (observer) {
                    observer.disconnect();
                }
                scrollContainer = newScrollContainer;
                observer = createObserver(node, scrollContainer);
            }
        },
        destroy() {
            if (observer) {
                observer.disconnect();
            }
        }
    };
}

/**
 * For `visibilityObserver` action: creates an IntersectionObserver if needed
 * @param {DOMElement} node - the node to which IntersectionObserver is attached
 * @param {DOMElement|null} [scrollContainer] - scroll container:
 *    set to `null` if scroll is on document body;
 *    set to 'undefined' (not specified) if shouldn't create observer
 * @returns {IntersectionObserver|null}
 */
function createObserver(node, scrollContainer) {
    if (typeof scrollContainer === 'undefined') {
        return null;
    }

    //rootMarin is added to avoid browser rounding issues:
    //  sometimes parent has `bottom = 342`, but node has `bottom = 342.39`, and intersection is not detected
    const observerOptions = {
        threshold: 0,
        rootMargin: '1px 1px 1px 1px'
    };
    if (scrollContainer) {
        observerOptions.root = scrollContainer;
    }
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const isVisible = entry.isIntersecting;
            node.dispatchEvent(new CustomEvent('isVisible', { detail: isVisible }));
        });
    }, observerOptions);

    observer.observe(node);
    return observer;
}
